import { useState, useEffect, useCallback } from 'react';
import './Admin.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const CATEGORIES = ['buffet', 'rice', 'kottu', 'devilled', 'chapsuey', 'pizza', 'sides'];
const TOKEN_KEY = 'gemiya_admin_token';

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function saveToken(t) { localStorage.setItem(TOKEN_KEY, t); }
function dropToken() { localStorage.removeItem(TOKEN_KEY); }

// ── Login screen ──────────────────────────────────────────────────────────────

function LoginView({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login">
      <form className="admin-login-card" onSubmit={handleSubmit}>
        <img src="/gemiya-logo.png" alt="Gemiya" className="admin-login-logo" />
        <h1>Admin</h1>
        {error && <div className="admin-error">{error}</div>}
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoFocus
          required
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

// ── Dish modal (add / edit) ───────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '', name_fr: '', category: 'buffet',
  price: '', description: '', description_fr: '',
  sort_order: 0, photo: null,
};

function DishModal({ dish, defaultCategory, onSave, onClose }) {
  const [form, setForm]     = useState(() => dish
    ? { name: dish.name || '', name_fr: dish.name_fr || '', category: dish.category,
        price: dish.price || '', description: dish.description || '',
        description_fr: dish.description_fr || '', sort_order: dish.sort_order ?? 0, photo: null }
    : { ...EMPTY_FORM, category: defaultCategory }
  );
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(dish?.photo_data ?? null);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    set('photo', file);
    setPreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form, dish?.id); }
    finally { setSaving(false); }
  }

  return (
    <div
      className="admin-modal-backdrop"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="admin-modal">
        <div className="admin-modal-header">
          <h3>{dish?.id ? 'Edit Dish' : 'Add Dish'}</h3>
          <button className="admin-modal-close" type="button" onClick={onClose}>×</button>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-form-row">
            <label>Name (EN) *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} required />
          </div>
          <div className="admin-form-row">
            <label>Name (FR)</label>
            <input value={form.name_fr} onChange={e => set('name_fr', e.target.value)} />
          </div>
          <div className="admin-form-row">
            <label>Category *</label>
            <select value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="admin-form-row">
            <label>Price</label>
            <input value={form.price} onChange={e => set('price', e.target.value)} placeholder="13 €" />
          </div>
          <div className="admin-form-row">
            <label>Description (EN)</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} />
          </div>
          <div className="admin-form-row">
            <label>Description (FR)</label>
            <textarea value={form.description_fr} onChange={e => set('description_fr', e.target.value)} rows={2} />
          </div>
          <div className="admin-form-row">
            <label>Sort Order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={e => set('sort_order', Number(e.target.value))}
            />
          </div>
          <div className="admin-form-row">
            <label>Photo</label>
            <input type="file" accept="image/*" onChange={handleFile} />
            {preview && <img src={preview} alt="preview" className="admin-photo-preview" />}
          </div>

          <div className="admin-form-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

function Dashboard({ token, onLogout }) {
  const [dishes, setDishes]     = useState([]);
  const [category, setCategory] = useState('buffet');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null); // null | 'new' | dish-object

  const auth = { Authorization: `Bearer ${token}` };

  const loadDishes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/dishes?includeInactive=1&withPhotos=1`, { headers: auth });
      if (res.status === 401) { onLogout(); return; }
      setDishes(await res.json());
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadDishes(); }, [loadDishes]);

  const visible = dishes.filter(d => d.category === category);

  async function handleSave(form, dishId) {
    const url    = dishId ? `${API}/dishes/${dishId}` : `${API}/dishes`;
    const method = dishId ? 'PATCH' : 'POST';

    const { photo, ...fields } = form;
    const res  = await fetch(url, {
      method,
      headers: { ...auth, 'Content-Type': 'application/json' },
      body:    JSON.stringify(fields),
    });
    const saved = await res.json();

    if (photo) {
      const fd = new FormData();
      fd.append('photo', photo);
      await fetch(`${API}/dishes/${saved.id}/photo`, { method: 'POST', headers: auth, body: fd });
    }

    setModal(null);
    loadDishes();
  }

  async function handleToggle(dish) {
    await fetch(`${API}/dishes/${dish.id}`, {
      method:  'PATCH',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body:    JSON.stringify({ active: dish.active ? 0 : 1 }),
    });
    loadDishes();
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this dish from the menu?')) return;
    await fetch(`${API}/dishes/${id}`, { method: 'DELETE', headers: auth });
    loadDishes();
  }

  return (
    <div className="admin-root">
      <header className="admin-header">
        <img src="/gemiya-logo.png" alt="Gemiya" className="admin-header-logo" />
        <span>Menu Admin</span>
        <button className="admin-logout" onClick={onLogout}>Sign out</button>
      </header>

      <div className="admin-tabs">
        {CATEGORIES.map(c => (
          <button
            key={c}
            className={`admin-tab${c === category ? ' active' : ''}`}
            onClick={() => setCategory(c)}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="admin-content">
        <div className="admin-toolbar">
          <h2>{category}</h2>
          <button className="admin-btn-add" onClick={() => setModal('new')}>+ Add Dish</button>
        </div>

        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="admin-empty">No dishes in this category yet.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(dish => (
                <tr key={dish.id} className={dish.active ? '' : 'inactive'}>
                  <td>
                    {dish.photo_data
                      ? <img src={dish.photo_data} alt={dish.name} className="admin-thumb" />
                      : <div className="admin-thumb-empty">No photo</div>
                    }
                  </td>
                  <td>
                    <div className="admin-dish-name">{dish.name}</div>
                    {dish.name_fr     && <div className="admin-dish-fr">{dish.name_fr}</div>}
                    {dish.description && <div className="admin-dish-desc">{dish.description}</div>}
                  </td>
                  <td className="admin-price">{dish.price || '—'}</td>
                  <td>
                    <button
                      className={`admin-badge ${dish.active ? 'active' : 'hidden'}`}
                      onClick={() => handleToggle(dish)}
                    >
                      {dish.active ? 'Visible' : 'Hidden'}
                    </button>
                  </td>
                  <td className="admin-actions">
                    <button className="admin-btn-edit" onClick={() => setModal(dish)}>Edit</button>
                    <button className="admin-btn-del"  onClick={() => handleDelete(dish.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <DishModal
          dish={modal === 'new' ? null : modal}
          defaultCategory={category}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ── Entry point ───────────────────────────────────────────────────────────────

export default function Admin() {
  const [token, setTokenState] = useState(getToken);

  function handleLogin(t)  { saveToken(t); setTokenState(t); }
  function handleLogout()  { dropToken();  setTokenState(null); }

  if (!token) return <LoginView onLogin={handleLogin} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}
